import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Trash2, Edit2, ExternalLink } from "lucide-react";

interface TextSelection {
  start: number;
  end: number;
  text: string;
  originalText: string;
}

interface Comment {
  id: string;
  text: string;
  color: string;
  number: number;
  textSelections: TextSelection[];
  isGeneral: boolean;
}

interface CommentCardProps {
  comment: Comment;
  isHighlighted?: boolean;
  readOnly?: boolean;
  onEdit?: (commentId: string, text: string) => void;
  onDelete?: (commentId: string) => void;
  onClick?: () => void;
  showExternalLink?: boolean;
}

// Color mapping with explicit Tailwind classes to ensure they're included in build
const colorStyles = {
  blue: {
    bg: 'bg-blue-50',
    border: 'border-blue-200 border-l-4 border-l-blue-500',
    badge: 'bg-blue-500',
  },
  amber: {
    bg: 'bg-amber-50',
    border: 'border-amber-200 border-l-4 border-l-amber-500',
    badge: 'bg-amber-500',
  },
  orange: {
    bg: 'bg-orange-50',
    border: 'border-orange-200 border-l-4 border-l-orange-500',
    badge: 'bg-orange-500',
  },
  purple: {
    bg: 'bg-purple-50',
    border: 'border-purple-200 border-l-4 border-l-purple-500',
    badge: 'bg-purple-500',
  },
  green: {
    bg: 'bg-green-50',
    border: 'border-green-200 border-l-4 border-l-green-500',
    badge: 'bg-green-500',
  },
  pink: {
    bg: 'bg-pink-50',
    border: 'border-pink-200 border-l-4 border-l-pink-500',
    badge: 'bg-pink-500',
  },
  teal: {
    bg: 'bg-teal-50',
    border: 'border-teal-200 border-l-4 border-l-teal-500',
    badge: 'bg-teal-500',
  },
};

const grayBadge = 'bg-gray-500';

export const CommentCard = ({
  comment,
  isHighlighted = false,
  readOnly = false,
  onEdit,
  onDelete,
  onClick,
  showExternalLink = false,
}: CommentCardProps) => {
  const colorStyle = colorStyles[comment.color as keyof typeof colorStyles] || colorStyles.blue;

  return (
    <Card
      id={`comment-${comment.id}`}
      data-comment-card-id={comment.id}
      className={`p-3 relative transition-all duration-300 ${colorStyle.bg} ${colorStyle.border} ${
        isHighlighted ? 'ring-2 ring-primary shadow-lg scale-[1.02]' : ''
      } ${onClick ? 'cursor-pointer hover:shadow-md' : ''}`}
      onClick={onClick}
    >
      {/* Number badge or G badge */}
      <div
        className={`absolute left-2 top-3 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold ${
          comment.isGeneral ? grayBadge : colorStyle.badge
        }`}
      >
        {comment.isGeneral ? 'G' : comment.number}
      </div>

      <div className="ml-10">
        {/* External link icon for readonly navigation (top-right corner) */}
        {showExternalLink && (
          <div className="absolute top-3 right-3">
            <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
          </div>
        )}

        {/* Text selection preview */}
        {!comment.isGeneral && comment.textSelections.length > 0 && (
          <div className="mb-2 text-xs">
            <span className="font-semibold">[{comment.number}]</span>{' '}
            <span className="italic">
              "{comment.textSelections[0].text.substring(0, 50)}
              {comment.textSelections[0].text.length > 50 ? '...' : ''}"
            </span>
          </div>
        )}

        {/* General comment label */}
        {comment.isGeneral && (
          <div className="text-xs font-semibold mb-2 opacity-70">
            [G] General Comment
          </div>
        )}

        {/* Comment text */}
        <p className="text-sm whitespace-pre-wrap">{comment.text}</p>

        {/* Edit/Delete buttons (editable mode only) */}
        {!readOnly && onEdit && onDelete && (
          <div className="flex justify-end mt-2 gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(comment.id, comment.text);
              }}
              className="h-7 px-2 hover:bg-primary/10"
            >
              <Edit2 className="h-3 w-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(comment.id);
              }}
              className="h-7 px-2 text-destructive hover:text-destructive"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};
